import React, { useState, useEffect } from 'react';
import { Box, Typography, Modal, IconButton, Grid, Skeleton, Button, Avatar, Dialog, DialogContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { HousekeepingService } from '../api/services/housekeepingService';
import { useSelector } from 'react-redux';
import { toast } from '../utils/toast';

export default function TaskDetailsModal({ open, onClose, taskId }) {
    const { user } = useSelector(state => state.auth);
    const tenantId = user?.tenantId || '1';
    
    const [task, setTask] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [previewPopup, setPreviewPopup] = useState(null);

    useEffect(() => {
        if (open && taskId) {
            fetchTaskDetails();
        } else {
            setTask(null);
        }
    }, [open, taskId]);

    const fetchTaskDetails = async () => {
        setIsLoading(true);
        try {
            const data = await HousekeepingService.getTaskById(taskId, tenantId);
            setTask(data?.data || data);
        } catch (error) {
            console.error('Failed to fetch task details:', error);
            toast.error(error.message || 'Failed to fetch task details');
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Completed':
                return { bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' };
            case 'In Progress':
                return { bgcolor: '#e3f2fd', color: '#1976d2', border: '1px solid #bbdefb' };
            case 'Inspection':
                return { bgcolor: '#fff3e0', color: '#e65100', border: '1px solid #ffe0b2' };
            case 'Cancelled':
                return { bgcolor: '#ffebee', color: '#c62828', border: '1px solid #ffcdd2' };
            case 'Ready':
            default:
                return { bgcolor: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd' };
        }
    };

    const getPriorityStyle = (priority) => {
        switch (priority) {
            case 'Urgent':
                return { bgcolor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' };
            case 'High':
                return { bgcolor: '#ffedd5', color: '#ea580c', border: '1px solid #fed7aa' };
            case 'Medium':
                return { bgcolor: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe' };
            case 'Low':
                return { bgcolor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
            default:
                return { bgcolor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
        }
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const d = new Date(dateStr);
            return d.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateStr;
        }
    };

    const handleModalClose = (event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
    };

    return (
        <>
            <Modal open={open} onClose={handleModalClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '92%', sm: 540 },
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#ffffff',
                    borderRadius: 4,
                    boxShadow: '0 24px 48px rgba(0,0,0,0.14)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    outline: 'none',
                    overflow: 'hidden',
                }}>
                    {/* Header */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: { xs: 2.5, sm: '20px 28px' },
                        borderBottom: '1px solid #f1f5f9',
                        flexShrink: 0,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                width: 42,
                                height: 42,
                                borderRadius: '12px',
                                bgcolor: 'rgba(25, 118, 210, 0.08)',
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <AssignmentOutlinedIcon />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="700" sx={{ lineHeight: 1.2 }}>
                                    Task Details
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Task overview and current progress
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{
                                color: 'text.secondary',
                                bgcolor: 'rgba(0,0,0,0.04)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Scrollable Body */}
                    <Box sx={{
                        p: { xs: 2.5, sm: '20px 28px' },
                        overflowY: 'auto',
                        flex: 1,
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#cbd5e1',
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: '#94a3b8',
                        },
                    }}>
                        {isLoading || !task ? (
                            <Box sx={{ py: 2 }}>
                                <Skeleton variant="rectangular" height={70} sx={{ borderRadius: 3, mb: 2.5 }} />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}><Skeleton variant="rectangular" height={75} sx={{ borderRadius: 2.5 }} /></Grid>
                                    <Grid item xs={6}><Skeleton variant="rectangular" height={75} sx={{ borderRadius: 2.5 }} /></Grid>
                                    <Grid item xs={6}><Skeleton variant="rectangular" height={75} sx={{ borderRadius: 2.5 }} /></Grid>
                                    <Grid item xs={6}><Skeleton variant="rectangular" height={75} sx={{ borderRadius: 2.5 }} /></Grid>
                                </Grid>
                                <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2.5, mt: 2.5 }} />
                            </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {/* Hero Room & Status Banner */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            bgcolor: '#f8fafc',
                            borderRadius: 3,
                            border: '1px solid #e2e8f0',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <MeetingRoomOutlinedIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="600" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Location
                                    </Typography>
                                    <Typography variant="h6" fontWeight="800" sx={{ lineHeight: 1.1, color: '#0f172a' }}>
                                        Room {task.roomNumber || task.room || task.roomId || 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{
                                ...getStatusStyle(task.status),
                                px: 1.75,
                                py: 0.6,
                                borderRadius: '20px',
                                fontWeight: '700',
                                fontSize: '0.82rem',
                                letterSpacing: '0.2px',
                                textTransform: 'capitalize',
                            }}>
                                {task.status || 'Ready'}
                            </Box>
                        </Box>

                        {/* 4 Info Cards Grid */}
                        <Grid container spacing={1.75}>
                            {/* Task Type Card */}
                            <Grid item xs={12} sm={6}>
                                <Box sx={{
                                    p: 1.75,
                                    borderRadius: 2.5,
                                    border: '1px solid #f1f5f9',
                                    bgcolor: '#fafbfc',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                        <CleaningServicesOutlinedIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                        <Typography variant="caption" color="text.secondary" fontWeight="600">
                                            TASK TYPE
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1" fontWeight="700" color="#1e293b">
                                        {task.taskType || task.type || task.taskTypeId || 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Priority Level Card */}
                            <Grid item xs={12} sm={6}>
                                <Box sx={{
                                    p: 1.75,
                                    borderRadius: 2.5,
                                    border: '1px solid #f1f5f9',
                                    bgcolor: '#fafbfc',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                        <FlagOutlinedIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                        <Typography variant="caption" color="text.secondary" fontWeight="600">
                                            PRIORITY LEVEL
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'inline-flex' }}>
                                        <Box sx={{
                                            ...getPriorityStyle(task.priority),
                                            px: 1.25,
                                            py: 0.35,
                                            borderRadius: '16px',
                                            fontWeight: '700',
                                            fontSize: '0.78rem',
                                        }}>
                                            {task.priority || 'Medium'}
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Assigned Staff Card */}
                            <Grid item xs={12} sm={6}>
                                <Box sx={{
                                    p: 1.75,
                                    borderRadius: 2.5,
                                    border: '1px solid #f1f5f9',
                                    bgcolor: '#fafbfc',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                        <PersonOutlineOutlinedIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                        <Typography variant="caption" color="text.secondary" fontWeight="600">
                                            ASSIGNED STAFF
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar sx={{ width: 22, height: 22, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                                            {((task.assignedStaff?.fullName || task.assignedStaff?.name || task.assignedTo || 'U').charAt(0)).toUpperCase()}
                                        </Avatar>
                                        <Typography variant="body2" fontWeight="700" color="#1e293b">
                                            {task.assignedStaff?.fullName || task.assignedStaff?.name || task.assignedTo || task.assignedStaffId || 'Unassigned'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Due Date & Time Card */}
                            <Grid item xs={12} sm={6}>
                                <Box sx={{
                                    p: 1.75,
                                    borderRadius: 2.5,
                                    border: '1px solid #f1f5f9',
                                    bgcolor: '#fafbfc',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                        <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                        <Typography variant="caption" color="text.secondary" fontWeight="600">
                                            DUE DATE & TIME
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" fontWeight="700" color="#1e293b">
                                        {formatDateTime(task.dueBy)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Cancellation Reason Alert Box (if cancelled) */}
                        {task.status === 'Cancelled' && task.cancellationReason && (
                            <Box sx={{
                                p: 2,
                                borderRadius: 2.5,
                                bgcolor: '#fef2f2',
                                border: '1px solid #fecaca',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5,
                            }}>
                                <ErrorOutlineIcon sx={{ color: '#ef4444', fontSize: 20, mt: 0.2 }} />
                                <Box>
                                    <Typography variant="caption" fontWeight="700" color="#991b1b" sx={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                        Cancellation Reason
                                    </Typography>
                                    <Typography variant="body2" fontWeight="600" color="#b91c1c" sx={{ mt: 0.2 }}>
                                        {task.cancellationReason}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Staff Task Notes */}
                        <Box sx={{
                            p: 2,
                            borderRadius: 2.5,
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                <NotesOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                    Staff Task Notes
                                </Typography>
                            </Box>
                            <Typography variant="body2" color={task.notes ? '#334155' : 'text.secondary'} sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                {task.notes || 'No descriptive notes provided.'}
                            </Typography>
                        </Box>

                        {/* Media & Attachments */}
                        {(() => {
                            const rawMedia = task.proofImages || task.media || task.attachments || task.photos || task.images || (task.mediaUrl ? [task.mediaUrl] : (task.attachmentUrl ? [task.attachmentUrl] : []));
                            const parsedMedia = Array.isArray(rawMedia)
                                ? rawMedia.map(m => typeof m === 'string' ? m : (m?.url || m?.fileUrl || ''))
                                : (typeof rawMedia === 'string' && rawMedia ? [rawMedia] : []);
                            const validMedia = parsedMedia.filter(Boolean);

                            if (validMedia.length === 0) return null;

                            return (
                                <Box sx={{
                                    p: 2,
                                    borderRadius: 2.5,
                                    bgcolor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                                        <AttachFileIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                        <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                            Attached Media ({validMedia.length})
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                        {validMedia.map((url, idx) => {
                                            const isImg = typeof url === 'string' && (/\.(jpeg|jpg|gif|png|webp|svg)($|\?)/i.test(url) || url.startsWith('data:image'));
                                            const fileName = typeof url === 'string' ? url.split('/').pop().split('?')[0] : `Attachment ${idx + 1}`;
                                            return (
                                                <Box
                                                    key={idx}
                                                    onClick={() => isImg ? setPreviewPopup(url) : window.open(url, '_blank')}
                                                    sx={{
                                                        width: 90,
                                                        height: 90,
                                                        borderRadius: 2,
                                                        border: '1px solid #e2e8f0',
                                                        overflow: 'hidden',
                                                        bgcolor: '#fff',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        transition: 'all 0.2s',
                                                        '&:hover': { transform: 'scale(1.03)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
                                                    }}
                                                >
                                                    {isImg ? (
                                                        <Box
                                                            component="img"
                                                            src={url}
                                                            alt={fileName}
                                                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <Box sx={{ p: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                            <InsertDriveFileOutlinedIcon sx={{ fontSize: 28, color: '#64748b', mb: 0.5 }} />
                                                            <Typography variant="caption" noWrap sx={{ maxWidth: 70, fontSize: '0.62rem', color: '#475569' }}>
                                                                {fileName}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            );
                        })()}
                    </Box>
                )}
                    </Box>

                    {/* Fixed Footer */}
                    <Box sx={{
                        p: { xs: 2, sm: '16px 28px 20px 28px' },
                        borderTop: '1px solid #f1f5f9',
                        bgcolor: '#ffffff',
                        flexShrink: 0,
                    }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={onClose}
                            sx={{
                                py: 1,
                                borderRadius: 2.5,
                                textTransform: 'none',
                                fontWeight: '600',
                                color: '#475569',
                                borderColor: '#cbd5e1',
                                '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
                            }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Media Image Preview Dialog */}
            <Dialog open={!!previewPopup} onClose={() => setPreviewPopup(null)} maxWidth="md">
                <DialogContent sx={{ p: 1, position: 'relative', bgcolor: '#000', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <IconButton
                        onClick={() => setPreviewPopup(null)}
                        sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {previewPopup && (
                        <Box component="img" src={previewPopup} alt="Preview" sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
